import React, { useEffect, useRef } from 'react';
import { Animated, ViewStyle, StyleProp } from 'react-native';
import { createFadeInAnimation, createSlideInAnimation } from '../utils/animations';

interface AnimatedCardProps {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  delay?: number;
  animationType?: 'fade' | 'slide' | 'scale';
}

export const AnimatedCard: React.FC<AnimatedCardProps> = ({
  children,
  style,
  delay = 0,
  animationType = 'fade',
}) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;

  useEffect(() => {
    const animations = [];

    if (animationType === 'fade' || animationType === 'scale') {
      animations.push(createFadeInAnimation(fadeAnim, 400, delay));
    }

    if (animationType === 'slide') {
      animations.push(createSlideInAnimation(slideAnim, 30, 400, delay));
      animations.push(createFadeInAnimation(fadeAnim, 400, delay));
    }

    if (animationType === 'scale') {
      animations.push(
        Animated.spring(scaleAnim, {
          toValue: 1,
          tension: 100,
          friction: 8,
          delay: delay + 100,
          useNativeDriver: true,
        })
      );
    }

    Animated.parallel(animations).start();
  }, []);

  const getAnimatedStyle = () => {
    const baseStyle = {
      opacity: fadeAnim,
    };

    if (animationType === 'slide') {
      return {
        ...baseStyle,
        transform: [{ translateY: slideAnim }],
      };
    }

    if (animationType === 'scale') {
      return {
        ...baseStyle,
        transform: [{ scale: scaleAnim }],
      };
    }

    return baseStyle;
  };

  return (
    <Animated.View style={[style, getAnimatedStyle()]}>
      {children}
    </Animated.View>
  );
};